import { NavigateNext } from '@mui/icons-material';
import {
    Button,
    Divider,
    darken,
    IconButton,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import ChipLabel from '/@/components/chip-label';
import useUnitProvider from '/@/core/context/unit';
import type { UnitConverter } from '/@/core/units';
import { useAllCatalogProducts, useCatalogProductsByBrands } from '/@/hooks/catalog';
import { useUserLocations } from '/@/hooks/user';
import { _ } from '/@/locale';
import { getCatalogBrand, getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import { ALL_BRANDS_SLUG } from '/@/views/product-exploration/list';

type Ranking = {
    minEnvironment: number | null;
    maxEnvironment: number | null;
    isFeatured: boolean;
    value?: number;
    m?: number;
    b?: number;
    [key: string]: unknown;
};
export const getMaxAndMinEnvironmentFromRankings = (
    rankings: Ranking[] = [],
    newRankings: Ranking[] = [],
    units: UnitConverter
) => {
    const getMinMax = (arr: Ranking[]) => {
        if (arr.length === 0) return;
        const firstElement = arr[0]!;
        let minEnvironment = firstElement.minEnvironment;
        let maxEnvironment = firstElement.maxEnvironment;

        for (const ranking of arr) {
            if (!ranking.isFeatured) continue;
            if (minEnvironment && (!ranking.minEnvironment || ranking.minEnvironment < minEnvironment)) {
                minEnvironment = ranking.minEnvironment;
            }
            if (maxEnvironment && (!ranking.maxEnvironment || ranking.maxEnvironment > maxEnvironment)) {
                maxEnvironment = ranking.maxEnvironment;
            }
        }

        return {
            minEnvironment: minEnvironment ? units.toYield(minEnvironment, 'corn', 0) : null,
            maxEnvironment: maxEnvironment ? units.toYield(maxEnvironment, 'corn', 0) : null,
            baseMinEnvironment: minEnvironment ?? null,
            baseMaxEnvironment: maxEnvironment ?? null,
        };
    };

    let environments = getMinMax(newRankings.filter((r) => r.isFeatured && r?.value && r.value > 0));
    if (environments) return environments;
    environments = getMinMax(rankings.filter((r) => r.isFeatured && ((r.m && r.m > 0) || (r.b && r.b > 0))));
    if (!environments)
        return {
            minEnvironment: null,
            maxEnvironment: null,
            label: '-',
            baseMinEnvironment: null,
            baseMaxEnvironment: null,
        };

    const minEnvironment = !environments.minEnvironment ? null : environments.minEnvironment;
    const maxEnvironment = !environments.maxEnvironment ? null : environments.maxEnvironment;

    if (!minEnvironment && !maxEnvironment)
        return { minEnvironment, maxEnvironment, label: '-', baseMinEnvironment: null, baseMaxEnvironment: null };
    if (!minEnvironment)
        return {
            minEnvironment,
            maxEnvironment,
            label: `< ${maxEnvironment!.toString()}`,
            baseMinEnvironment: null,
            baseMaxEnvironment: environments.baseMaxEnvironment,
        };
    if (!maxEnvironment)
        return {
            minEnvironment,
            maxEnvironment,
            label: `> ${minEnvironment!.toString()}`,
            baseMinEnvironment: environments.baseMinEnvironment,
            baseMaxEnvironment: null,
        };
    return {
        minEnvironment,
        maxEnvironment,
        label: `${minEnvironment.value} - ${maxEnvironment!.toString()}`,
        baseMinEnvironment: environments.baseMinEnvironment,
        baseMaxEnvironment: environments.baseMaxEnvironment,
    };
};

/**
 * One positioning bucket a product covers: the display label plus its numeric
 * bounds (in the user's yield unit). The bounds are kept so consumers can sort
 * or compare buckets without re-parsing the localized label.
 */
export type PositioningBucket = {
    label: string;
    /** Lower bound; null means open ("< max"). */
    min: number | null;
    /** Upper bound; null means open ("> min"). */
    max: number | null;
};

/**
 * Return the distinct positioning buckets a product COVERS — one per featured
 * ranking row, labelled exactly like getMaxAndMinEnvironmentFromRankings.
 *
 * Options stay fully data-driven (no hardcoded buckets): the labels are whatever
 * the ranking rows define. Because the data is split into single-bucket rows,
 * a product with rows 0-6000 and 6000-9000 surfaces under both "< 60" and
 * "60 - 90". Uses the same table selection as the positioning label: featured
 * new_rankings (value>0) if any, otherwise featured rankings (m/b>0).
 */
export const getPositioningBucketsFromRankings = (
    rankings: Ranking[] = [],
    newRankings: Ranking[] = [],
    units: UnitConverter
): PositioningBucket[] => {
    const featuredNew = newRankings.filter((r) => r.isFeatured && r?.value && r.value > 0);
    const rows = featuredNew.length
        ? featuredNew
        : rankings.filter((r) => r.isFeatured && ((r.m && r.m > 0) || (r.b && r.b > 0)));

    const buckets = new Map<string, PositioningBucket>();
    for (const row of rows) {
        const min = row.minEnvironment ? units.toYield(row.minEnvironment, 'corn', 0) : null;
        const max = row.maxEnvironment ? units.toYield(row.maxEnvironment, 'corn', 0) : null;
        if (!min && !max) continue;

        let label: string;
        if (!min) label = `< ${max!.toString()}`;
        else if (!max) label = `> ${min.toString()}`;
        else label = `${min.value} - ${max.toString()}`;

        if (!buckets.has(label)) {
            buckets.set(label, { label, min: min?.value ?? null, max: max?.value ?? null });
        }
    }
    return Array.from(buckets.values());
};

/**
 * Ascending comparator for positioning buckets: the open-low bucket ("< X")
 * first, then by lower bound, with the open-high bucket ("> X") last within a
 * tie. Sorting on the numeric bounds — not the rendered label — keeps the order
 * correct regardless of the locale's thousands separator.
 */
export const comparePositioningBuckets = (a: PositioningBucket, b: PositioningBucket): number =>
    (a.min ?? Number.NEGATIVE_INFINITY) - (b.min ?? Number.NEGATIVE_INFINITY) ||
    (a.max ?? Number.POSITIVE_INFINITY) - (b.max ?? Number.POSITIVE_INFINITY);

export const mapPositionChips = (products: any[]) => {
    const colors = ['#B8F2C7', '#D3FFD7', '#F2F2B6', '#FFBECE'];

    // Deduplicate positions by label
    const allPositions = Array.from(new Map(products.map((p) => [p.position.label, p.position])).values());

    // Helper to safely get a bound value
    const getBound = (env: { value: number } | null | undefined, fallback: number) => env?.value ?? fallback;

    // Sort valid positions
    const sorted = allPositions
        .map((item, index) => ({ ...item, index }))
        .filter((item) => item.minEnvironment || item.maxEnvironment)
        .sort((a, b) => {
            const minA = getBound(a.minEnvironment, Number.NEGATIVE_INFINITY);
            const minB = getBound(b.minEnvironment, Number.NEGATIVE_INFINITY);
            if (minA !== minB) return minA - minB;

            const maxA = getBound(a.maxEnvironment, Number.POSITIVE_INFINITY);
            const maxB = getBound(b.maxEnvironment, Number.POSITIVE_INFINITY);
            return maxA - maxB;
        });

    // Assign colors to sorted items
    sorted.forEach((item, i) => {
        item.color = colors[i < colors.length ? i % colors.length : colors.length - 1];
    });

    // Build a map for label -> color
    const labelColorMap = new Map(sorted.map((item) => [item.label, item.color]));

    return products.map((p) => {
        const chipColor = labelColorMap.get(p.position.label) ?? '#e4e4e4'; // fallback color
        return {
            ...p,
            position: (
                <ChipLabel
                    label={p.position.label}
                    type='custom'
                    background={chipColor}
                    color={darken(chipColor, 0.7)}
                />
            ),
        };
    });
};

const ProductCatalog = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState<number>(0);

    const units = useUnitProvider();
    const { i18n } = useTranslation();

    const cropProtectionBrand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));

    const country = useSelector(getSessionCountry);
    const cornBrands = useSelector((state) => getCatalogBrandsByCrop(state, 'corn'));
    const userLocationsQuery = useUserLocations({ level: 2, pageSize: 100000 }, { enabled: !!country });

    const cornProductsQuery = useCatalogProductsByBrands(cornBrands, {
        country: country!.code!,
        crop: 'corn',
        zoneIds: userLocationsQuery.data?.results?.flatMap((l) => l.zones)
            ? Array.from(new Set(userLocationsQuery.data.results.flatMap((l) => l.zones)))
            : undefined,
    });

    const cornProducts = useMemo(() => {
        let products = cornProductsQuery.data?.results || [];

        products = mapPositionChips(
            products.map((p) => ({
                ...p,
                position: getMaxAndMinEnvironmentFromRankings(p.rankings, p.newRankings, units),
            }))
        );

        const seenPositions = new Set();
        const filteredProducts = products
            .filter((p) => p.catalogZoneId)
            .filter((p) => {
                const positionLabel = p.position?.props?.label;
                if (!positionLabel || seenPositions.has(positionLabel)) {
                    return false;
                }
                seenPositions.add(positionLabel);
                return true;
            })
            .slice(0, 6);

        return filteredProducts;
    }, [cornProductsQuery.data?.results]);

    const cropProtectionProductsQuery = useAllCatalogProducts({
        brand: cropProtectionBrand?.name,
        crop: 'crop_protection',
        country: country!.code!,
        cropProtectionCrop: undefined,
        cropProtectionProductType: undefined,
    });

    const CONFIGS = useMemo(
        () => [
            {
                label: _('Híbridos'),
                columns: [
                    { label: _('Híbrido'), value: (p) => p.name },
                    { label: _('Zona'), value: (p) => _(p.catalogZone).split(' - ')[0] }, // FIXME: Harcoded for Argentina
                    {
                        label: _('Ambiente'),
                        value: (p) => p.position,
                    },
                ],
                fromMonth: 0, // Enero
                toMonth: cropProtectionBrand && cropProtectionProductsQuery.data?.results?.length > 0 ? 11 : 11, // a Agosto
                type: 'corn',
            },
            ...(cropProtectionBrand && cropProtectionProductsQuery.data?.results?.length > 0
                ? [
                      {
                          label: _('Protección'),
                          columns: [
                              { label: _('Cultivo'), value: (p) => p.crops.map((c) => _(c.name)).join(', ') },
                              {
                                  label: _('Problemática'),
                                  value: (p) => _(p.type),
                              },
                              { label: _('Nombre'), value: (p) => p.name },
                          ],
                          fromMonth: 0, // Enero
                          toMonth: 7, // a Agosto
                          type: 'crop_protection',
                          subtype: ['Herbicide', 'Insecticide', 'Fungicide'],
                      },
                      {
                          label: _('Insecticidas'),
                          columns: [
                              { label: _('Cultivo'), value: (p) => p.crops.map((c) => _(c.name)).join(', ') },
                              { label: _('Problemática'), value: (p) => _(p.type) },
                              { label: _('Nombre'), value: (p) => p.name },
                          ],
                          fromMonth: 8, // Septiembre
                          toMonth: 11, // a Diciembre
                          type: 'crop_protection',
                          subtype: ['Insecticide'],
                      },
                      {
                          label: _('Fungicidas'),
                          columns: [
                              { label: _('Cultivo'), value: (p) => p.crops.map((c) => _(c.name)).join(', ') },
                              { label: _('Problemática'), value: (p) => _(p.type) },
                              { label: _('Nombre'), value: (p) => p.name },
                          ],
                          fromMonth: 8, // Septiembre
                          toMonth: 11, // a Diciembre
                          type: 'crop_protection',
                          subtype: ['Fungicide'],
                      },
                  ]
                : []),
        ],
        [units, i18n.resolvedLanguage, cropProtectionProductsQuery]
    );

    const activeConfigs = useMemo(() => {
        const month = new Date().getMonth();
        return CONFIGS.filter((config) => month >= config.fromMonth && month <= config.toMonth);
    }, [CONFIGS]);

    const cropProtectionProducts = useMemo(() => {
        const allProducts = cropProtectionProductsQuery.data?.results || [];
        const subtypes = activeConfigs?.[tab]?.subtype || [];

        if (subtypes.length <= 1) return (allProducts?.filter((p) => p.type.en === subtypes[0]) || []).slice(0, 6);

        // Group products by subtype and take 2 from each
        const productsBySubtype = subtypes.map((subtype) =>
            allProducts.filter((p) => p.type.en === subtype).slice(0, 2)
        );

        // Flatten the array to get all products
        return productsBySubtype.flat();
    }, [activeConfigs, cropProtectionProductsQuery.data?.results, tab]);

    const handleTabChange = useCallback((_, value) => setTab(value), []);

    const handleViewAllClick = useCallback(() => {
        navigate(
            reverse('productExploration.list', {
                params: {
                    type: activeConfigs?.[tab]?.type,
                    slug: activeConfigs?.[tab]?.type === 'corn' ? ALL_BRANDS_SLUG : cropProtectionBrand.slug,
                },
            })
        );
    }, [navigate, activeConfigs, tab, cropProtectionBrand]);

    const handleH2HClick = useCallback(() => {
        mpTrackEvent('H2H open', { entry_source: 'rv-home-products' });
    }, []);

    const navigateToProduct = useCallback(
        (product, type, brandSlug) => {
            // Look up the matching brand's real slug (computed backend-side via slugify) rather
            // than deriving one from the name client-side - a name with a space or accent would
            // produce a slug matching no brand and silently fall back to every brand.
            const matchedBrand = cornBrands.find((b) => b.id === product?.brand?.id);
            const params = {
                type,
                slug: matchedBrand?.slug || brandSlug,
                productId: product.id,
            };
            navigate(reverse('productExploration.detail', { params }));
        },
        [cornBrands]
    );

    const renderTableRows = (
        products: any[],
        columns: { label: string; value: (p: any) => any }[],
        type: 'corn' | 'crop_protection',
        brandSlug: string
    ) => {
        return products.map((product) => (
            <TableRow key={product.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                {columns.map((col) => (
                    <TableCell key={col.label}>
                        <Typography variant='body2' color='primary'>
                            {col.value(product)}
                        </Typography>
                    </TableCell>
                ))}
                <TableCell size='small' width='24px'>
                    <IconButton
                        size='small'
                        onClick={() => navigateToProduct(product, type, brandSlug)}
                        color='secondary'
                    >
                        <NavigateNext color='secondary' />
                    </IconButton>
                </TableCell>
            </TableRow>
        ));
    };

    const renderSkeletonRows = (columns: { label: string }[]) => {
        return Array.from({ length: 6 }, (_, i) => (
            <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                {columns.map((col) => (
                    <TableCell key={col.label}>
                        <Skeleton variant='text' width='100%' />
                    </TableCell>
                ))}
                <TableCell size='small' height='24px'>
                    <Skeleton variant='rounded' width='100%' />
                </TableCell>
            </TableRow>
        ));
    };

    const isLgOrLower = useMediaQuery((theme) => theme.breakpoints.down('lg'));

    return (
        <Stack
            sx={{
                background: 'white',
                border: (t) => `1px solid ${t.palette.divider}`,
                flex: 1,
                minHeight: 0,
            }}
        >
            <Stack sx={{ p: 2 }} spacing={1}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant='fullWidth'
                    textColor='secondary'
                    indicatorColor='secondary'
                >
                    {activeConfigs.map((c, i) => (
                        <Tab key={i} value={i} label={c.label} />
                    ))}
                </Tabs>
                <Table
                    size={isLgOrLower ? 'small' : 'medium'}
                    sx={{
                        '& .MuiTableCell-root': {
                            px: 0.5,
                            py: 1,
                        },
                        tableLayout: 'fixed',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {activeConfigs?.[tab]!.columns.map((col) => (
                                <TableCell
                                    key={col.label}
                                    sx={{
                                        fontWeight: 'bold',
                                        width: `${100 / activeConfigs[tab]!.columns.length}%`,
                                    }}
                                >
                                    <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                        {col.label}
                                    </Typography>
                                </TableCell>
                            ))}
                            <TableCell size='small' width='24px' />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {activeConfigs?.[tab]?.type === 'corn' &&
                            renderTableRows(
                                cornProducts,
                                activeConfigs[tab].columns,
                                activeConfigs?.[tab]?.type,
                                cornBrands[0]?.slug
                            )}
                        {activeConfigs?.[tab]?.type === 'corn' &&
                            cornProductsQuery.isValidating &&
                            renderSkeletonRows(activeConfigs[tab].columns)}

                        {activeConfigs?.[tab]?.type === 'crop_protection' &&
                            renderTableRows(
                                cropProtectionProducts,
                                activeConfigs[tab].columns,
                                activeConfigs?.[tab]?.type,
                                cropProtectionBrand?.slug
                            )}
                        {activeConfigs?.[tab]?.type === 'crop_protection' &&
                            cropProtectionProductsQuery.isValidating &&
                            renderSkeletonRows(activeConfigs[tab].columns)}
                    </TableBody>
                </Table>
            </Stack>
            <Divider sx={{ mt: 'auto' }} />
            <Stack direction='row' p={2} spacing={2}>
                <Button color='primary' variant='contained' onClick={handleViewAllClick}>
                    {_('Ver todos')}
                </Button>
                {/* <Button color='primary' variant='text' onClick={handleH2HClick}>
                    {_('Comparación H2H')}
                </Button> */}
            </Stack>
        </Stack>
    );
};

export default ProductCatalog;
